import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stepper,
  Step,
  StepLabel,
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Calculate from '@mui/icons-material/Calculate';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import type { Component, CreateComponentDto } from 'shared';
import { DataCategory, RegulatoryRequirement } from 'shared';
import { COMPONENT_TYPES } from './componentTypes';
import { getComponentTypeDescription } from './componentTypeDescriptions';

interface ComponentFormWizardProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateComponentDto) => Promise<void>;
  component?: Component;
  currentSpaceId: string;
}

export const ComponentFormWizard: React.FC<ComponentFormWizardProps> = ({
  open,
  onClose,
  onSubmit,
  component,
  currentSpaceId,
}) => {
  const { t, i18n } = useTranslation();

  const steps = [
    t('component.wizard.step1'),
    t('component.wizard.step2'),
    t('component.wizard.step3'),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState<CreateComponentDto>({
    name: component?.name || '',
    type: component?.type || 'application',
    description: component?.description || '',
    spaceId: currentSpaceId,
    isGlobal: component?.isGlobal || false,
    dataCategories: component?.dataCategories || [],
    regulatoryRequirements: component?.regulatoryRequirements || [],
    availabilityRequirement: component?.availabilityRequirement,
    confidentialityScore: component?.confidentialityScore || 1.0,
    integrityScore: component?.integrityScore || 1.0,
    availabilityScore: component?.availabilityScore || 1.0,
    environment: component?.environment || 'production',
    status: component?.status || 'active',
    notes: component?.notes || '',
  });

  // Update form data when component prop changes (for edit mode)
  useEffect(() => {
    if (open) {
      setFormData({
        name: component?.name || '',
        type: component?.type || 'application',
        description: component?.description || '',
        spaceId: currentSpaceId,
        isGlobal: component?.isGlobal || false,
        dataCategories: component?.dataCategories || [],
        regulatoryRequirements: component?.regulatoryRequirements || [],
        availabilityRequirement: component?.availabilityRequirement,
        confidentialityScore: component?.confidentialityScore || 1.0,
        integrityScore: component?.integrityScore || 1.0,
        availabilityScore: component?.availabilityScore || 1.0,
        environment: component?.environment || 'production',
        status: component?.status || 'active',
        notes: component?.notes || '',
      });
      setActiveStep(0);
      setError('');
    }
  }, [open, component, currentSpaceId]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Ensure numeric fields are actually numbers, not strings
      const sanitizedData: CreateComponentDto = {
        ...formData,
        confidentialityScore: Number(formData.confidentialityScore) || 1.0,
        integrityScore: Number(formData.integrityScore) || 1.0,
        availabilityScore: Number(formData.availabilityScore) || 1.0,
      };
      await onSubmit(sanitizedData);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setError('');
    onClose();
  };

  const handleDataCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      dataCategories: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleRegulatoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      regulatoryRequirements:
        typeof value === 'string' ? value.split(',') : value,
    });
  };

  const calculateScores = () => {
    let confScore = 1.0;
    let intScore = 1.0;
    let availScore = 1.0;

    // Calculate C/I scores based on simplified data categories
    if (formData.dataCategories && formData.dataCategories.length > 0) {
      // SPECIAL_CATEGORY_DATA (GDPR Art. 9) - highest sensitivity
      if (
        formData.dataCategories.includes(DataCategory.SPECIAL_CATEGORY_DATA)
      ) {
        confScore = 9.0;
        intScore = 9.0;
      }
      // FINANCIAL_DATA (PCI-DSS) - high sensitivity
      else if (formData.dataCategories.includes(DataCategory.FINANCIAL_DATA)) {
        confScore = 8.0;
        intScore = 8.0;
      }
      // AUTHENTICATION_DATA - high integrity, medium confidentiality
      else if (
        formData.dataCategories.includes(DataCategory.AUTHENTICATION_DATA)
      ) {
        confScore = 7.0;
        intScore = 9.0;
      }
      // PERSONAL_DATA - medium sensitivity
      else if (formData.dataCategories.includes(DataCategory.PERSONAL_DATA)) {
        confScore = 5.0;
        intScore = 5.0;
      }
      // BUSINESS_DATA - medium-low sensitivity
      else if (formData.dataCategories.includes(DataCategory.BUSINESS_DATA)) {
        confScore = 4.0;
        intScore = 6.0;
      }
      // SYSTEM_DATA - low confidentiality, medium integrity
      else if (formData.dataCategories.includes(DataCategory.SYSTEM_DATA)) {
        confScore = 3.0;
        intScore = 5.0;
      }
      // PUBLIC_DATA or NO_SENSITIVE_DATA - minimal sensitivity
      else {
        confScore = 2.0;
        intScore = 3.0;
      }
    }

    // Calculate availability score based on RTO requirement
    if (formData.availabilityRequirement === '1_hour') {
      availScore = 9.0;
    } else if (formData.availabilityRequirement === '4_hours') {
      availScore = 7.0;
    } else if (formData.availabilityRequirement === '1_day') {
      availScore = 5.0;
    } else if (formData.availabilityRequirement === '1_week') {
      availScore = 3.0;
    } else {
      availScore = 1.0;
    }

    setFormData({
      ...formData,
      confidentialityScore: confScore,
      integrityScore: intScore,
      availabilityScore: availScore,
    });
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(formData.name && formData.type);
      case 1:
        return true;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label={t('component.name')}
              fullWidth
              required
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />

            <Autocomplete
              fullWidth
              options={[...(COMPONENT_TYPES as unknown as string[])].sort(
                (a, b) =>
                  t(`componentTypes:${a}`).localeCompare(
                    t(`componentTypes:${b}`),
                    i18n.language
                  )
              )}
              value={formData.type}
              onChange={(_event, newValue) => {
                if (newValue) {
                  setFormData({ ...formData, type: newValue });
                }
              }}
              getOptionLabel={option => t(`componentTypes:${option}`)}
              renderInput={params => (
                <TextField {...params} label={t('component.type')} required />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const typeInfo = getComponentTypeDescription(option);
                return (
                  <li key={key} {...otherProps}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>{t(`componentTypes:${option}`)}</span>
                      {typeInfo && (
                        <Chip
                          label={typeInfo.examples}
                          size="small"
                          variant="outlined"
                          sx={{
                            ml: 1,
                            fontSize: '0.7rem',
                            height: '20px',
                            color: 'text.secondary',
                            borderColor: 'divider',
                            maxWidth: '200px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => option === value}
              autoHighlight
              openOnFocus
            />

            {/* Component type info box */}
            {formData.type && getComponentTypeDescription(formData.type) && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: 'info.lighter',
                  border: '1px solid',
                  borderColor: 'info.light',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <InfoOutlined
                  sx={{ color: 'info.main', fontSize: '1.2rem', mt: 0.2 }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('component.typeExamples')}:{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="medium"
                    >
                      {getComponentTypeDescription(formData.type)?.examples}
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            )}

            <TextField
              label={t('component.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isGlobal}
                  onChange={e =>
                    setFormData({ ...formData, isGlobal: e.target.checked })
                  }
                />
              }
              label={t('component.isGlobal')}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth sx={{ maxHeight: 400 }}>
              <InputLabel>{t('component.dataCategories')}</InputLabel>
              <Select
                multiple
                value={formData.dataCategories || []}
                onChange={handleDataCategoryChange}
                input={<OutlinedInput label={t('component.dataCategories')} />}
                renderValue={selected =>
                  `${selected.length} ${t('component.selected')}`
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {Object.values(DataCategory).map(category => (
                  <MenuItem key={category} value={category}>
                    <Checkbox
                      checked={
                        (formData.dataCategories || []).indexOf(category) > -1
                      }
                    />
                    <ListItemText
                      primary={t(`categories:${category}.name`)}
                      secondary={t(`categories:${category}.description`)}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ maxHeight: 400 }}>
              <InputLabel>{t('component.regulatoryRequirements')}</InputLabel>
              <Select
                multiple
                value={formData.regulatoryRequirements || []}
                onChange={handleRegulatoryChange}
                input={
                  <OutlinedInput
                    label={t('component.regulatoryRequirements')}
                  />
                }
                renderValue={selected =>
                  `${selected.length} ${t('component.selected')}`
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {Object.values(RegulatoryRequirement).map(requirement => (
                  <MenuItem key={requirement} value={requirement}>
                    <Checkbox
                      checked={
                        (formData.regulatoryRequirements || []).indexOf(
                          requirement
                        ) > -1
                      }
                    />
                    <ListItemText
                      primary={t(`regulatory:${requirement}.name`)}
                      secondary={t(`regulatory:${requirement}.description`)}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('component.availabilityRequirement')}</InputLabel>
              <Select
                value={formData.availabilityRequirement || ''}
                label={t('component.availabilityRequirement')}
                onChange={e =>
                  setFormData({
                    ...formData,
                    availabilityRequirement: e.target.value,
                  })
                }
              >
                <MenuItem value="1_hour">
                  {t('component.availability.1_hour')}
                </MenuItem>
                <MenuItem value="4_hours">
                  {t('component.availability.4_hours')}
                </MenuItem>
                <MenuItem value="1_day">
                  {t('component.availability.1_day')}
                </MenuItem>
                <MenuItem value="1_week">
                  {t('component.availability.1_week')}
                </MenuItem>
                <MenuItem value="none">
                  {t('component.availability.none')}
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={calculateScores}
              startIcon={<Calculate />}
              fullWidth
            >
              {t('component.calculateScores')}
            </Button>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 2,
              }}
            >
              <TextField
                label={t('component.confidentialityScore')}
                type="number"
                inputProps={{ min: 1, max: 10, step: 0.1 }}
                value={formData.confidentialityScore}
                onChange={e =>
                  setFormData({
                    ...formData,
                    confidentialityScore: parseFloat(e.target.value),
                  })
                }
                helperText="1.0-10.0"
              />
              <TextField
                label={t('component.integrityScore')}
                type="number"
                inputProps={{ min: 1, max: 10, step: 0.1 }}
                value={formData.integrityScore}
                onChange={e =>
                  setFormData({
                    ...formData,
                    integrityScore: parseFloat(e.target.value),
                  })
                }
                helperText="1.0-10.0"
              />
              <TextField
                label={t('component.availabilityScore')}
                type="number"
                inputProps={{ min: 1, max: 10, step: 0.1 }}
                value={formData.availabilityScore}
                onChange={e =>
                  setFormData({
                    ...formData,
                    availabilityScore: parseFloat(e.target.value),
                  })
                }
                helperText="1.0-10.0"
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>{t('component.environment')}</InputLabel>
              <Select
                value={formData.environment}
                label={t('component.environment')}
                onChange={e =>
                  setFormData({ ...formData, environment: e.target.value })
                }
              >
                <MenuItem value="production">
                  {t('component.env.production')}
                </MenuItem>
                <MenuItem value="staging">
                  {t('component.env.staging')}
                </MenuItem>
                <MenuItem value="development">
                  {t('component.env.development')}
                </MenuItem>
                <MenuItem value="test">{t('component.env.test')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('component.status')}</InputLabel>
              <Select
                value={formData.status}
                label={t('component.status')}
                onChange={e =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="active">
                  {t('component.statusValues.active')}
                </MenuItem>
                <MenuItem value="inactive">
                  {t('component.statusValues.inactive')}
                </MenuItem>
                <MenuItem value="deprecated">
                  {t('component.statusValues.deprecated')}
                </MenuItem>
                <MenuItem value="planned">
                  {t('component.statusValues.planned')}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('component.notes')}
              fullWidth
              multiline
              rows={4}
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {component ? t('component.edit.title') : t('component.create')}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            {t('common.back')}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!isStepValid(activeStep) || loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : activeStep === steps.length - 1 ? (
            t('common.save')
          ) : (
            t('common.next')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
